import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Download, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { downloadPng, downloadSvg } from '@/lib/export-logo'

type ExportFormat = 'svg' | 'png'

interface VariationCardProps {
    index: string
    tag: string
    name: string
    description: string
    fileName: string
    pngWidth: number
    stageClassName?: string
    children: ReactNode
}

const FORMATS: ExportFormat[] = ['svg', 'png']

export const VariationCard: FC<VariationCardProps> = ({
    index,
    tag,
    name,
    description,
    fileName,
    pngWidth,
    stageClassName,
    children
}): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const [pending, setPending] = useState<ExportFormat | null>(null)
    const [failed, setFailed] = useState(false)

    const handleExport = async (format: ExportFormat): Promise<void> => {
        const svg = stageRef.current?.querySelector<SVGSVGElement>('svg[data-logo-export]')

        if (!svg) {
            setFailed(true)
            return
        }

        setFailed(false)
        setPending(format)

        try {
            if (format === 'svg') downloadSvg(svg, fileName)
            else await downloadPng(svg, fileName, pngWidth)
        }
        catch (error) {
            console.error(error)
            setFailed(true)
        }
        finally {
            setPending(null)
        }
    }

    return (
        <article className='group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition-colors duration-300 hover:border-brand-red/30'>
            <div
                ref={stageRef}
                className={cn(
                    'relative isolate flex aspect-[16/10] items-center justify-center overflow-hidden p-6 sm:p-10 lg:p-12',
                    stageClassName
                )}
            >
                {children}
            </div>

            <div className='flex flex-1 flex-col gap-6 border-t border-border p-6 sm:p-7'>
                <div>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-brand-red'>
                        {index}
                        <span className='text-muted-foreground'> / {tag}</span>
                    </p>

                    <h3 className='mt-3 font-heading text-xl font-semibold tracking-tight text-foreground'>
                        {name}
                    </h3>

                    <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                        {description}
                    </p>
                </div>

                <div className='mt-auto flex flex-wrap items-center gap-2'>
                    {FORMATS.map(format => (
                        <button
                            key={format}
                            type='button'
                            disabled={pending !== null}
                            aria-label={`Download the ${name.toLowerCase()} as ${format.toUpperCase()}`}
                            onClick={() => {
                                void handleExport(format)
                            }}
                            className='inline-flex h-10 items-center gap-2 rounded-full border border-border bg-secondary px-4 text-sm font-semibold text-foreground transition-colors hover:border-brand-red/60 hover:bg-brand-red/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red disabled:cursor-wait disabled:opacity-60'
                        >
                            {pending === format
                                ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                                : <Download aria-hidden='true' className='size-4 text-brand-red' />}
                            {format.toUpperCase()}
                        </button>
                    ))}

                    <p role='status' aria-live='polite' className={cn('text-xs text-brand-red', failed && 'w-full')}>
                        {failed ? "That download didn't start. Please try again." : ''}
                    </p>
                </div>
            </div>
        </article>
    )
}
